<script setup lang="ts">
/**
 * Purpose picker for a participant-initiated payment.
 *
 * Reproduces the certifid-100-vision prototype's intent sheet
 * (`app/components/portal/buyer-initiated/MakePaymentIntentSheet.vue` on main):
 * a centred title and subtitle, then one bordered button per purpose carrying a
 * tinted icon chip, a title, a helper line and a chevron. Copy is verbatim from
 * that prototype's en.json.
 *
 * Closing-fund purposes lead, earnest money first, so visual weight tracks
 * consequence. The prototype's third option, "Something else", is not rendered:
 * PaymentPurposeType has no Other member, so no BFF can offer it yet.
 *
 * The list is always whatever dashboard-bff sent. Offering a purpose the company
 * has not enabled produces a permanent spinner in the payments flow.
 *
 * No reconciliation banner, matching the prototype: when the chosen purpose
 * already has an open title-company task, the bootstrap adopts that task rather
 * than telling the buyer about it here.
 */
import BottomSheetModal from '@Components/BottomSheetModal.vue';
import type { InitiablePurpose } from '@Use/useParticipantInitiatedPayment';

defineProps<{
    open: boolean;
    purposes: InitiablePurpose[];
    isStarting?: boolean;
}>();

const emit = defineEmits<{
    'update:open': [value: boolean];
    select: [purpose: string];
}>();

const close = () => emit('update:open', false);
</script>

<template>
    <BottomSheetModal
        :open="open"
        dialog-label="What are you paying for?"
        @update:open="(val: boolean) => { if (!val) close(); }"
    >
        <div class="purpose-sheet portal-step-enter">
            <div class="purpose-sheet__header">
                <h2 class="purpose-sheet__title">What are you paying for?</h2>
                <p class="purpose-sheet__subtitle">
                    We will route your payment the right way based on what it is for.
                </p>
            </div>

            <div class="purpose-sheet__list">
                <button
                    v-for="purpose in purposes"
                    :key="purpose.value"
                    type="button"
                    class="purpose-sheet__item"
                    :disabled="isStarting"
                    :data-test="`purpose-option-${purpose.value}`"
                    @click="emit('select', purpose.value)"
                >
                    <span class="purpose-sheet__item-chip" aria-hidden="true">
                        <app-icon :icon="purpose.icon" />
                    </span>
                    <span class="purpose-sheet__item-content">
                        <span class="purpose-sheet__item-title">{{ purpose.label }}</span>
                        <span class="purpose-sheet__item-helper">{{ purpose.helper }}</span>
                    </span>
                    <app-icon icon="cd-ic-caret-right" class="purpose-sheet__chevron" aria-hidden="true" />
                </button>
            </div>
        </div>
    </BottomSheetModal>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.purpose-sheet {
    display: flex;
    flex-direction: column;

    &__header {
        text-align: center;
        // BottomSheetModal pins its close control absolutely and reserves no space
        // for it, so the centred title has to clear it or the two collide.
        // PropertySheet pads its title down for the same reason.
        padding-top: 32px;
        margin-bottom: 24px;

        @media (min-width: 768px) {
            padding-top: 36px;
        }
    }

    &__title {
        font-size: $text-portal-h3;
        font-weight: 700;
        line-height: 1.25;
        color: $portal-text;
        margin: 0;
    }

    &__subtitle {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        margin: 8px 0 0;
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: $portal-item-gap;
    }

    &__item {
        display: flex;
        align-items: center;
        gap: $portal-card-gap;
        width: 100%;
        text-align: left;
        padding: $portal-card-padding;
        border-radius: $portal-card-radius;
        border: 1px solid $portal-border;
        background: $white;
        // Buttons don't inherit the page font-family by default.
        font-family: inherit;
        cursor: pointer;
        transition: border-color 0.15s ease, transform 0.15s ease;

        &:hover {
            border-color: $black-low;
        }

        &:active {
            transform: scale(0.99);
        }

        &:focus-visible {
            outline: 2px solid $theme-primary;
            outline-offset: 2px;
        }

        &:disabled {
            opacity: 0.6;
            cursor: default;
            transform: none;
        }
    }

    &__item-chip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        // $portal-section-icon is 40px, 46px on coarse mobile, and is defined
        // identically in the prototype, so it matches its rendered 40px chip.
        // Deliberately not $portal-priming-icon, which is what the prototype's
        // own markup names: that token is 40px there but 56px, 64px here, so the
        // shared name would oversize the row against the design.
        width: $portal-section-icon;
        height: $portal-section-icon;
        // The prototype uses an arbitrary rounded-[12px] here rather than one of
        // the radius tokens it also carries, so this follows suit. The nearest
        // token, $portal-modal-radius, is coincidentally 12px but means a modal.
        border-radius: 12px;
        // Tinted chip, matching the prototype's bg-portal-primary-light with a
        // brand-coloured mark, against the card's solid brand chip.
        background: $theme-primary-lightest;

        :deep(i) {
            font-size: 18px;
            color: $theme-primary;
        }
    }

    &__item-content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__item-title {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        // leading-snug on the row title, leading-relaxed on its helper, matching
        // the prototype.
        line-height: 1.375;
        color: $portal-text;
    }

    &__item-helper {
        font-size: $text-portal-small;
        line-height: 1.625;
        color: $portal-text-mid;
    }

    &__chevron {
        flex-shrink: 0;

        :deep(i) {
            font-size: 20px;
            color: $portal-text-mid;
        }
    }
}
</style>
